import React from "react";
import {
    getSensitivePreferenceMode,
    shouldMaskSensitiveMedia,
    type SensitiveMaskingDirective,
} from "@/main/common/SensitivePreference";
import {
    isSensitiveUnlocked,
    unlockSensitive,
    type SensitiveUnlockKind,
} from "@/main/common/SensitiveUnlockStore";

export type SensitivePageUnlockOptions = {
    kind: SensitiveUnlockKind;
    id?: string | null;
    isSensitive?: boolean | null;
    isSpoiler?: boolean | null;
};

export type SensitivePageUnlock = {
    /** Whether this page needs a consent step at all, given the viewer's preference. */
    shouldGate: boolean;
    /** True when the page's content may render unmasked — either never gated, or consented to. */
    revealed: boolean;
    reveal: () => void;
    /** What the page's nested cards should do about masking. Pass straight down as `sensitiveMasking`. */
    sensitiveMasking: SensitiveMaskingDirective;
};

/**
 * Owns the "has this viewer opened this sensitive page" decision for a gated page.
 *
 * The state lives here, in the page, rather than inside `SensitivePageGate`, because the page needs the same
 * boolean for two different jobs: whether to show the consent modal, and whether to stop masking the cards
 * *inside* it. Keeping it in the gate is what left the second job unwired — passing the modal unblurred the
 * page while every nested card stayed blurred and demanded its own separate click.
 *
 * Note `isSpoiler` gates regardless of the sensitive preference: SHOW is consent to see sensitive content,
 * not consent to be spoiled. A single reveal still opens the whole page — the two axes decide whether to
 * *ask*, not how much one answer covers.
 */
export function useSensitivePageUnlock({
    kind,
    id,
    isSensitive = false,
    isSpoiler = false,
}: SensitivePageUnlockOptions): SensitivePageUnlock {
    const shouldGate = Boolean(isSpoiler)
        || shouldMaskSensitiveMedia(Boolean(isSensitive), getSensitivePreferenceMode());

    const [revealed, setRevealed] = React.useState(
        () => !shouldGate || isSensitiveUnlocked(kind, id),
    );

    React.useEffect(() => {
        setRevealed(!shouldGate || isSensitiveUnlocked(kind, id));
    }, [kind, id, shouldGate]);

    const reveal = React.useCallback(() => {
        unlockSensitive(kind, id);
        setRevealed(true);
    }, [kind, id]);

    const sensitiveMasking = React.useMemo<SensitiveMaskingDirective>(() => {
        // Only a page that actually asked for consent may waive masking for the things inside it. Gating on
        // `revealed` alone would unmask sensitive cards on a page that was never gated — e.g. a BLUR viewer
        // on a perfectly ordinary profile, where `revealed` is trivially true because nothing was hidden.
        if (shouldGate && revealed) {
            return { disableSensitiveMasking: true };
        }

        // Not yet consented: a HIDE viewer who deliberately opened this page should see its related items
        // blurred rather than silently absent. Never tightens SHOW or BLUR — see SensitivePreference.ts.
        return isSensitive ? { sensitivePreferenceFloor: "blur" } : {};
    }, [isSensitive, revealed, shouldGate]);

    return { shouldGate, revealed, reveal, sensitiveMasking };
}
